import { useEffect, useState, useCallback, useRef } from 'react';
import {
  Search, Trash2, RefreshCw, Mail, Inbox, Send, AlertCircle, X,
  Reply, MailOpen, Mail as MailIcon, CheckCheck, Square, CheckSquare,
} from 'lucide-react';
import { apiFetch } from '../lib/api';

interface OwnMailMessage {
  id: string; address: string; mail_from: string; subject: string | null;
  body_text: string | null; body_html?: string | null; received_at: number;
  is_read: number; is_sent: number;
}

const PAGE = 25;
const inputCls = 'w-full px-3 py-2 rounded-xl border border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm';

type StatusFilter = 'all' | 'unread' | 'read';

export default function OwnMail() {
  const [tab, setTab] = useState<'inbox' | 'compose'>('inbox');
  const [messages, setMessages] = useState<OwnMailMessage[]>([]);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [toFilter, setToFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [addresses, setAddresses] = useState<string[]>([]);
  const [domain, setDomain] = useState('inboxme.space');
  const [sendEnabled, setSendEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  // Multi-select
  const [selectMode, setSelectMode] = useState(false);
  const [checked, setChecked] = useState<Set<string>>(new Set());

  // Modal
  const [selected, setSelected] = useState<OwnMailMessage | null>(null);

  // Long-press timer for mobile select mode
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Compose form
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [sendOk, setSendOk] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const offset = (page - 1) * PAGE;
    const qs = new URLSearchParams({
      limit: String(PAGE), offset: String(offset),
      search, to: toFilter, status: statusFilter,
    });
    const res = await apiFetch(`/api/admin/own-mail?${qs}`);
    if (res.ok) {
      const d = await res.json();
      setMessages(d.messages || []);
      setTotal(d.total || 0);
      setUnreadCount(d.unread || 0);
      setAddresses(d.addresses || []);
      setDomain(d.domain || 'inboxme.space');
      setSendEnabled(!!d.send_enabled);
    }
    setLoading(false);
  }, [page, search, toFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [search, toFilter, statusFilter]);

  // Lock body scroll when modal open
  useEffect(() => {
    if (selected) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [selected]);

  // ── Select helpers ──
  const toggleCheck = (id: string) => setChecked(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const allChecked = messages.length > 0 && messages.every(m => checked.has(m.id));
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(messages.map(m => m.id)));

  const exitSelect = () => { setSelectMode(false); setChecked(new Set()); };

  // ── Long press on mobile → enter select mode ──
  const onRowPointerDown = (id: string) => {
    longPressTimer.current = setTimeout(() => {
      if (!selectMode) { setSelectMode(true); setChecked(new Set([id])); }
    }, 500);
  };
  const onRowPointerUp = () => {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
  };

  // ── Bulk actions ──
  const markAllRead = async () => {
    setMarkingAll(true);
    await apiFetch('/api/admin/own-mail/read-all', { method: 'POST' });
    setMarkingAll(false);
    setUnreadCount(0);
    load();
  };

  const bulkDelete = async () => {
    if (checked.size === 0) return;
    if (!confirm(`Delete ${checked.size} message${checked.size !== 1 ? 's' : ''}?`)) return;
    setBulkDeleting(true);
    await apiFetch('/api/admin/own-mail/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ ids: Array.from(checked) }),
    });
    setBulkDeleting(false);
    exitSelect();
    load();
  };

  const openMessage = async (msg: OwnMailMessage) => {
    // In select mode, toggle checkbox instead of opening
    if (selectMode) { toggleCheck(msg.id); return; }
    setSelected(msg);
    if (!msg.is_sent && !msg.is_read) {
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, is_read: 1 } : m));
      setUnreadCount(c => Math.max(0, c - 1));
      try {
        await apiFetch(`/api/admin/own-mail/${encodeURIComponent(msg.id)}/read`, { method: 'POST' });
      } catch { /* best-effort */ }
    }
  };

  const remove = async (id: string) => {
    if (!confirm('Delete this message?')) return;
    setDeleting(id);
    await apiFetch(`/api/admin/own-mail/${encodeURIComponent(id)}`, { method: 'DELETE' });
    setDeleting(null);
    setSelected(null);
    load();
  };

  const replyTo = (msg: OwnMailMessage) => {
    const replyAddr = msg.mail_from || '';
    const reSubject = msg.subject?.startsWith('Re:') ? msg.subject : `Re: ${msg.subject || '(no subject)'}`;
    const quoted = (msg.body_text || '').split('\n').map(l => `> ${l}`).join('\n');
    const quoteBlock = `\n\nOn ${new Date(msg.received_at * 1000).toLocaleString()}, ${replyAddr} wrote:\n${quoted}`;
    setTo(replyAddr);
    setSubject(reSubject);
    setBody(quoteBlock);
    setSelected(null);
    setTab('compose');
  };

  const send = async () => {
    setSendError(''); setSendOk(false);
    if (!to || !subject || !body) { setSendError('To, subject and body are required'); return; }
    setSending(true);
    try {
      const res = await apiFetch('/api/admin/own-mail/send', {
        method: 'POST',
        body: JSON.stringify({ to, subject, text: body }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) { setSendOk(true); setTo(''); setSubject(''); setBody(''); }
      else { setSendError(d.error || `Send failed (${res.status})`); }
    } catch { setSendError('Network error'); }
    setSending(false);
  };

  const pages = Math.max(1, Math.ceil(total / PAGE));
  const statusTabs: { key: StatusFilter; label: string; count?: number }[] = [
    { key: 'all', label: 'All', count: total },
    { key: 'unread', label: 'Unread', count: unreadCount },
    { key: 'read', label: 'Read' },
  ];

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-500 to-violet-600 bg-clip-text text-transparent">Own Mail</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Business addresses on <span className="font-mono font-medium">@{domain}</span> (contact@, support@ …)
            {' · '}{total.toLocaleString()} message{total !== 1 ? 's' : ''}
            {unreadCount > 0 && (
              <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 text-xs font-semibold">
                {unreadCount} unread
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button onClick={markAllRead} disabled={markingAll}
              className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition disabled:opacity-50">
              <CheckCheck size={15} /> {markingAll ? 'Marking…' : `Mark all read (${unreadCount})`}
            </button>
          )}
          <button onClick={load} className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl mb-5 w-fit">
        <button onClick={() => { setTab('inbox'); exitSelect(); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${tab === 'inbox' ? 'bg-white dark:bg-gray-700 shadow text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}>
          <Inbox size={15} /> Inbox
        </button>
        <button onClick={() => { setTab('compose'); exitSelect(); }}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${tab === 'compose' ? 'bg-white dark:bg-gray-700 shadow text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}>
          <Send size={15} /> Compose
        </button>
      </div>

      {tab === 'inbox' ? (
        <>
          {/* Bulk action bar — visible when selectMode is active */}
          {selectMode && (
            <div className="flex items-center gap-2 mb-4 p-3 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700/40 rounded-xl flex-wrap">
              <button onClick={toggleAll} className="flex items-center gap-1.5 text-sm font-medium text-indigo-700 dark:text-indigo-300 hover:underline">
                {allChecked ? <CheckSquare size={16} /> : <Square size={16} />}
                {allChecked ? 'Deselect all' : 'Select all'}
              </button>
              <span className="text-xs text-indigo-500 dark:text-indigo-400 font-semibold">{checked.size} selected</span>
              <div className="ml-auto flex items-center gap-2">
                {checked.size > 0 && (
                  <>
                    <button onClick={bulkDelete} disabled={bulkDeleting}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-50">
                      <Trash2 size={14} /> {bulkDeleting ? 'Deleting…' : `Delete (${checked.size})`}
                    </button>
                  </>
                )}
                <button onClick={exitSelect}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition">
                  <X size={14} /> Cancel
                </button>
              </div>
            </div>
          )}

          {/* Filters */}
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl">
              {statusTabs.map(t => (
                <button key={t.key} onClick={() => setStatusFilter(t.key)}
                  className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-semibold transition ${
                    statusFilter === t.key
                      ? 'bg-white dark:bg-gray-700 shadow text-gray-900 dark:text-gray-100'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}>
                  {t.key === 'unread' && <Mail size={13} />}
                  {t.key === 'read' && <MailOpen size={13} />}
                  {t.key === 'all' && <CheckCheck size={13} />}
                  {t.label}
                  {typeof t.count === 'number' && t.count > 0 && (
                    <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                      statusFilter === t.key
                        ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                        : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {t.count}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* Select mode toggle (visible always — checkbox icon) */}
            {!selectMode && messages.length > 0 && (
              <button onClick={() => setSelectMode(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition border border-transparent hover:border-indigo-200 dark:hover:border-indigo-800">
                <Square size={14} /> Select
              </button>
            )}

            <div className="relative flex-1 min-w-[200px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search subject, from, body…"
                className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm" />
            </div>
            {addresses.length > 1 && (
              <select value={toFilter} onChange={e => setToFilter(e.target.value)}
                className="px-3 py-2.5 rounded-xl border border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="">All addresses</option>
                {addresses.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            )}
          </div>

          {/* Messages */}
          {loading && messages.length === 0 ? (
            <div className="py-16 text-center text-gray-400">
              <RefreshCw size={24} className="animate-spin mx-auto mb-3" />
              <p>Loading messages…</p>
            </div>
          ) : messages.length === 0 ? (
            <div className="py-16 text-center text-gray-400 bg-white/80 dark:bg-gray-800/80 rounded-2xl border border-gray-200/60 dark:border-gray-700/60">
              <Inbox size={40} className="mx-auto mb-3 opacity-30" />
              <p className="font-medium">
                {statusFilter === 'unread' ? 'No unread messages' : statusFilter === 'read' ? 'No read messages' : 'No messages yet'}
              </p>
              <p className="text-sm mt-1 opacity-70 max-w-md mx-auto">
                Mail sent to business addresses on <span className="font-mono">@{domain}</span> (contact@, support@, info@ …)
                lands here automatically via Cloudflare Email Routing.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {messages.map((msg) => {
                const isSent = !!msg.is_sent;
                const isUnread = !isSent && !msg.is_read;
                const isChecked = checked.has(msg.id);
                return (
                  <button key={msg.id}
                    onClick={() => openMessage(msg)}
                    onPointerDown={() => onRowPointerDown(msg.id)}
                    onPointerUp={onRowPointerUp}
                    onPointerLeave={onRowPointerUp}
                    className={`w-full flex items-center gap-3 p-3.5 rounded-2xl transition text-left border ${
                      isChecked
                        ? 'bg-indigo-100/80 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-700'
                        : isUnread
                          ? 'bg-indigo-50/60 dark:bg-indigo-900/15 border-indigo-200/60 dark:border-indigo-800/40 hover:bg-indigo-100/60 dark:hover:bg-indigo-900/25'
                          : 'bg-white/80 dark:bg-gray-800/80 border-gray-200/60 dark:border-gray-700/60 hover:bg-gray-50/60 dark:hover:bg-gray-700/30'
                    }`}
                  >
                    {/* Checkbox — always visible in select mode, also hidden on mobile when not in select mode */}
                    {selectMode && (
                      <span className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition ${
                        isChecked
                          ? 'bg-indigo-600 border-indigo-600 text-white'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        {isChecked && <CheckSquare size={14} />}
                      </span>
                    )}

                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                      isSent
                        ? 'bg-gradient-to-br from-amber-500 to-orange-600'
                        : isUnread
                          ? 'bg-gradient-to-br from-indigo-500 to-violet-600'
                          : 'bg-gradient-to-br from-gray-400 to-gray-500'
                    }`}>
                      {isSent ? <Send size={15} className="text-white" /> : <MailIcon size={15} className="text-white" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm truncate ${isUnread ? 'font-bold text-gray-900 dark:text-gray-50' : 'font-medium text-gray-800 dark:text-gray-200'}`}>
                        {msg.subject || '(no subject)'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                        {isSent ? (
                          <>To: <span className="font-mono">{msg.mail_from || '—'}</span></>
                        ) : (
                          <>From: <span className="font-mono">{msg.mail_from || '—'}</span> · To: <span className="font-mono">{msg.address}</span></>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-2.5 shrink-0">
                      {isUnread && <span className="w-2.5 h-2.5 rounded-full bg-indigo-500 shrink-0" />}
                      {isSent && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Sent</span>
                      )}
                      <span className="text-xs text-gray-400 whitespace-nowrap hidden sm:inline">
                        {new Date(msg.received_at * 1000).toLocaleString()}
                      </span>
                      <button
                        onClick={(e) => { e.stopPropagation(); remove(msg.id); }}
                        disabled={deleting === msg.id}
                        title="Delete"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition disabled:opacity-50 shrink-0"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between mt-4 text-sm text-gray-600 dark:text-gray-400">
              <span>Page <b>{page}</b> of <b>{pages}</b> · {total.toLocaleString()} total</span>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700">← Prev</button>
                <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700">Next →</button>
              </div>
            </div>
          )}
        </>
      ) : (
        /* ── Compose ── */
        <div className="max-w-2xl">
          {!sendEnabled && (
            <div className="flex items-start gap-3 mb-5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 rounded-2xl px-4 py-3.5">
              <AlertCircle size={18} className="text-amber-500 shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800 dark:text-amber-300">
                <p className="font-semibold">Send is not configured yet</p>
                <p className="mt-1">
                  Set a Resend API key on the Worker so outbound mail works:
                  <code className="block mt-1.5 font-mono text-xs bg-amber-100 dark:bg-amber-900/40 rounded px-2 py-1">
                    wrangler secret put RESEND_API_KEY
                  </code>
                </p>
              </div>
            </div>
          )}

          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/60 dark:border-gray-700/60 p-6 space-y-4">
            <div>
              <label className="block text-xs font-medium mb-1.5 text-gray-600 dark:text-gray-400">From</label>
              <input value={`contact@${domain}`} disabled
                className={inputCls + ' opacity-60 cursor-not-allowed font-mono'} />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5 text-gray-600 dark:text-gray-400">To *</label>
              <input value={to} onChange={e => setTo(e.target.value)} type="email" placeholder="recipient@example.com"
                className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5 text-gray-600 dark:text-gray-400">Subject *</label>
              <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject"
                className={inputCls} />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1.5 text-gray-600 dark:text-gray-400">Body *</label>
              <textarea value={body} onChange={e => setBody(e.target.value)} rows={10}
                placeholder="Message text…"
                className={inputCls + ' font-mono resize-y min-h-[200px]'} />
            </div>

            {sendError && (
              <p className="text-sm text-red-500 flex items-center gap-1.5"><AlertCircle size={14} /> {sendError}</p>
            )}
            {sendOk && (
              <p className="text-sm text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                <Mail size={14} /> Sent successfully. A copy is saved in the Inbox tab.
              </p>
            )}

            <div className="flex justify-end">
              <button onClick={send} disabled={sending || !sendEnabled}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-indigo-500 to-violet-600 text-white rounded-xl text-sm font-medium shadow hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed">
                <Send size={15} /> {sending ? 'Sending…' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Message Modal ── */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setSelected(null); }}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <div className="relative w-full max-w-3xl max-h-[85vh] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 p-5 border-b border-gray-100 dark:border-gray-700 shrink-0">
              <div className="flex items-start gap-3 min-w-0">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${
                  selected.is_sent
                    ? 'bg-gradient-to-br from-amber-500 to-orange-600'
                    : 'bg-gradient-to-br from-indigo-500 to-violet-600'
                }`}>
                  {selected.is_sent ? <Send size={16} className="text-white" /> : <MailIcon size={16} className="text-white" />}
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 leading-snug">
                    {selected.subject || '(no subject)'}
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    {selected.is_sent ? (
                      <>To: <span className="font-mono">{selected.mail_from}</span></>
                    ) : (
                      <>From: <span className="font-mono">{selected.mail_from}</span></>
                    )}
                    <span className="mx-1">·</span>
                    To: <span className="font-mono">{selected.address}</span>
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {new Date(selected.received_at * 1000).toLocaleString()}
                    {selected.is_sent && (
                      <span className="ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Sent</span>
                    )}
                  </p>
                </div>
              </div>
              <button onClick={() => setSelected(null)}
                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {selected.body_html ? (
                <div className="prose prose-sm dark:prose-invert max-w-none bg-gray-50 dark:bg-gray-900/40 rounded-xl p-4 border border-gray-100 dark:border-gray-700"
                  dangerouslySetInnerHTML={{ __html: selected.body_html }} />
              ) : (
                <pre className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono bg-gray-50 dark:bg-gray-900/40 rounded-xl p-4 leading-relaxed">
                  {selected.body_text || '(no text body)'}
                </pre>
              )}
            </div>

            <div className="flex items-center justify-between gap-3 p-4 border-t border-gray-100 dark:border-gray-700 shrink-0 bg-gray-50/50 dark:bg-gray-900/20">
              <span className="text-xs text-gray-400 font-mono truncate max-w-[200px]">ID: {selected.id}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => remove(selected.id)} disabled={deleting === selected.id}
                  className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition disabled:opacity-50">
                  <Trash2 size={14} /> {deleting === selected.id ? 'Deleting…' : 'Delete'}
                </button>
                {!selected.is_sent && (
                  <button onClick={() => replyTo(selected)}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium bg-gradient-to-r from-indigo-500 to-violet-600 text-white shadow hover:opacity-90 transition">
                    <Reply size={14} /> Reply
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
